import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface PendingApprovalPageProps {
  status: 'allowed' | 'pending_enable' | 'disabled' | 'unknown';
  message?: string;
}

const PendingApprovalPage: React.FC<PendingApprovalPageProps> = ({ status, message }) => {
  const { user, signOut, retryAuth } = useAuth();
  const [retrying, setRetrying] = useState(false);

  const isDisabled = status === 'disabled';
  const isUnreachable = message?.toLowerCase().includes('unreachable');

  const heading = isDisabled ? 'Access disabled' : 'Account pending enablement';
  const body = message || (isDisabled
    ? 'Your account is currently disabled. Contact an administrator for access.'
    : 'Your account was created and now requires admin enablement in Firebase Authentication.');

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryAuth();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-ts-cream flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-sm border border-ts-border p-8">
        <h1 className="font-serif text-2xl font-semibold text-ts-charcoal mb-3">
          {heading}
        </h1>
        <p className="text-gray-600 mb-6">
          {body}
        </p>
        <div className="rounded-lg bg-ts-cream p-4 mb-6 border border-ts-border">
          <p className="text-sm text-gray-500">Signed in as</p>
          <p className="text-sm font-medium text-ts-charcoal break-all">
            {user?.email || 'Unknown user'}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {isUnreachable && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="w-full px-4 py-3 rounded-lg bg-ts-gold hover:bg-yellow-600 text-white font-medium transition-colors disabled:opacity-50"
            >
              {retrying ? 'Connecting…' : 'Try again'}
            </button>
          )}
          <button
            onClick={signOut}
            className="w-full px-4 py-3 rounded-lg bg-ts-navy hover:bg-ts-charcoal text-white font-medium transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalPage;